// Axis and corner-tag rules of the page's charts (DAA-2-23, DAA-2-25), computed the way the prototype
// computes them. No imports, so the Node test runner can load it.

// Float noise (0.30000000000000004) never reaches a tick label.
const clean = (value) => Number(value.toPrecision(12))

// The smallest of 1, 2, 2.5, 5 or 10 × 10ⁿ that is at least `value`.
export const niceMax = (value) => {
  if (!(value > 0)) return 1

  const magnitude = 10 ** Math.floor(Math.log10(value))
  const mantissa = value / magnitude

  for (const factor of [1, 2, 2.5, 5]) {
    if (mantissa <= factor) return clean(factor * magnitude)
  }

  return clean(10 * magnitude)
}

// The smallest whole step of at least `value` among 1, 1.5, 2, 2.5, 3, 4, 5, 6, 8 and 10 × 10ⁿ: finer
// than niceMax, so a count axis tops out just above the data (a peak of 200 gives 0–240, not 0–400).
const wholeStepUp = (value) => {
  if (value <= 1) return 1

  const magnitude = 10 ** Math.floor(Math.log10(value))

  for (const factor of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8]) {
    const step = clean(factor * magnitude)

    if (step >= value && Number.isInteger(step)) return step
  }

  return clean(10 * magnitude)
}

// The smallest of 1, 2, 5 or 10 × 10ⁿ that is at least `value`: a zoomed axis' step.
const roundStepUp = (value) => {
  const magnitude = 10 ** Math.floor(Math.log10(value))

  return clean(
    [1, 2, 5, 10].map((factor) => factor * magnitude).find((candidate) => candidate >= value)
  )
}

// Five ticks up to a rounded maximum (DAA-2-25): from 0 to niceMax(max × 1.05), or to `max` when a
// chart pins it. With `zoom` (rate, duration and average-price trends) the axis fits the data: a
// padded window on round steps, never below 0 for data that is not, and never above `max`.
// With `whole` (counts) the step is rounded instead of the top, to a whole round one (wholeStepUp), so
// the ticks never read 0, 1, 1, 2, 3 (DAA-2-26).
export const yAxisRange = (values, { zoom = false, max, whole = false, minStep = 0 } = {}) => {
  const finite = values.filter((value) => Number.isFinite(value))
  const dataMax = finite.length ? Math.max(...finite) : 0

  if (whole && !zoom && max == null) {
    const step = wholeStepUp((Math.max(0, dataMax) * 1.05) / 4)

    return { min: 0, max: 4 * step, step }
  }

  if (!zoom || !finite.length) {
    const top = max ?? niceMax(Math.max(0, dataMax) * 1.05)

    return { min: 0, max: top, step: clean(top / 4) }
  }

  const dataMin = Math.min(...finite)
  // A flat series gets a window of whole steps around it, so its five ticks read five different values.
  const isFlat = dataMax === dataMin
  const pad = isFlat
    ? Math.max(Math.abs(dataMax) * 0.1, 2)
    : Math.max((dataMax - dataMin) * 0.35, Math.abs(dataMax) * 0.02)
  // `minStep` keeps ticks written as whole percents from repeating ("25% 25% 26%").
  let step = Math.max(minStep, roundStepUp((dataMax - dataMin + 2 * pad) / 4))
  let low
  let top

  // Four steps from a round start must reach the data; if they fall short, the next round step does,
  // so every tick stays on a round value.
  do {
    low = Math.floor((dataMin - pad) / step) * step

    if (low < 0 && dataMin >= 0) low = 0

    top = low + 4 * step

    if (top < dataMax) step = roundStepUp(step * 1.01)
  } while (top < dataMax)

  // A pinned top caps the zoomed window too — a rate never passes 100% — and the window slides down
  // under it, on the same steps while it stays above 0.
  if (max != null && top > max) {
    top = max
    low = Math.max(0, top - 4 * step)
  }

  return { min: clean(low), max: clean(top), step: clean((top - low) / 4) }
}

// A date label ("Oct 20" in 12px) and the gap it needs to its neighbour, between centres.
const LABEL_SPACING = 56

// Which of `count` interval labels the x-axis prints: at most six (DAA-2-25), one whole step apart
// from the first, as the platform's date axes skip theirs — a fractional step rounded to whole days
// skips one day and not its neighbours, so a day looks missing (CHA1-4618). The last interval is
// labelled when the step lands on it. On a plot `width` px wide the step grows until no two labels
// touch, or until only the first and the last are left; `offset` is a bar chart's axis, its labels
// centred in their intervals.
export const xLabelIndexes = (count, { width = Infinity, offset = false } = {}) => {
  // Px between neighbouring intervals; without a measured width (NaN, Infinity) every gap fits.
  const gap = width / (offset ? count : count - 1)
  // Six labels or fewer.
  let step = Math.max(1, Math.ceil(count / 6))

  while (step < count - 1 && step * gap < LABEL_SPACING) {
    step++
  }

  const indexes = []

  for (let index = 0; index < count; index += step) {
    indexes.push(index)
  }

  // A bar axis centres a label in its interval, so one near the plot's right edge would be cut off;
  // it is left out, as the platform's axes leave the end of a period unlabelled.
  const last = indexes[indexes.length - 1]

  if (offset && indexes.length > 1 && (last + 0.5) * gap + LABEL_SPACING / 2 > width) {
    indexes.pop()
  }

  return new Set(indexes)
}

// A chart's corner tag (DAA-2-23): its granularity, "stacked · " in front for a stacked chart.
// A chart marked weekly in its section passes granularity "week" at every period length.
export const granularityTag = (granularity, { stacked = false } = {}) => {
  const unit = granularity === "week" ? "weekly" : "per day"

  return stacked ? `stacked · ${unit}` : unit
}
